/**
 * LanguagePicker — compact locale selector (globe icon + menu).
 *
 * Used in:
 *   - Onboarding page (top-right, before sign-in)
 *   - Admin shell user menu (after sign-in)
 *
 * Switches both i18next locale AND persists to localStorage.
 * For RTL locales (ar), sets document.dir = 'rtl'.
 */
import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemText, Typography } from '@mui/material';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LOCALES,
  LOCALE_NAMES,
  RTL_LOCALES,
  type SupportedLocale,
} from '../../i18n/config';
import { getAccessToken } from '../../auth/authService';
import { meApi } from '../../api/me';

interface LanguagePickerProps {
  /** Render as a full menu item (for embedding in a dropdown) vs icon button */
  variant?: 'icon' | 'menuItem';
}

export const LanguagePicker: React.FC<LanguagePickerProps> = ({ variant = 'icon' }) => {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const currentLocale = (i18n.language || 'en') as SupportedLocale;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleChange = (locale: SupportedLocale) => {
    i18n.changeLanguage(locale);
    // RTL support
    document.documentElement.dir = RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr';
    // Persist the worker's interface language to the backend so
    // send-time channels (push / email) render in their own language
    // (Worker.language, alembic 054). Best-effort + only when signed in
    // — the picker also renders pre-auth on the onboarding page where
    // there's no worker row yet, so we skip the call without a token and
    // swallow any failure (a language switch must never surface an error).
    if (getAccessToken()) {
      meApi.updateProfile({ language: locale }).catch(() => {});
    }
    handleClose();
  };

  if (variant === 'menuItem') {
    return (
      <>
        <MenuItem onClick={handleOpen}>
          <LanguageOutlinedIcon sx={{ fontSize: 18, mr: 1.5, color: 'text.secondary' }} />
          <ListItemText
            primary={LOCALE_NAMES[currentLocale] || currentLocale}
            primaryTypographyProps={{ fontSize: '0.875rem' }}
          />
        </MenuItem>
        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
          {SUPPORTED_LOCALES.map((locale) => (
            <MenuItem
              key={locale}
              selected={locale === currentLocale}
              onClick={() => handleChange(locale)}
              sx={{ minWidth: 160 }}
            >
              <ListItemText
                primary={LOCALE_NAMES[locale]}
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        sx={{
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          borderRadius: 1,
          px: 0.75,
          '&:hover': { bgcolor: 'action.hover' },
        }}
        title={LOCALE_NAMES[currentLocale]}
      >
        <LanguageOutlinedIcon sx={{ fontSize: 16 }} />
        <Typography
          component="span"
          sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', lineHeight: 1 }}
        >
          {currentLocale.split('-')[0].toUpperCase()}
        </Typography>
      </IconButton>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        {SUPPORTED_LOCALES.map((locale) => (
          <MenuItem
            key={locale}
            selected={locale === currentLocale}
            onClick={() => handleChange(locale)}
            sx={{ minWidth: 160 }}
          >
            <ListItemText
              primary={LOCALE_NAMES[locale]}
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguagePicker;
